import ApiService from './';
import { UpdateAutomationParams, UpdateBuildAutomationParams } from '../params/redux/automation';

/**
* @heading1: Authentication API Service
 *
 * @paragraph: This module provides a set of methods to interact with the Authentication API.
 * It handles operations like user login, signup, and logout.
 */

const AutomationApi = {
    /**
     * @heading3: Login Function
     *
     * @paragraph: Authenticates the user by verifying their credentials.
     *
     * @param: {LoginParams} data - The user credentials (email and password) to authenticate.
     * @returns: {Promise<LoginResponse>} - A promise that resolves to the login response containing authentication details.
     * @code:
     * AuthApi.login({ email: 'email', password: 'password123' })
     *   .then(response => console.log(response.token))
     *   .catch(error => console.error(error));
     * @separator:
     */
    createAutomation: (token: string, automation_name: string, client_id: number) => {
        // Perform a POST request to the login endpoint with the user data
        return ApiService.apiHit({
            url: 'create-automation',
            token,
            method: 'post',
            data: { automation_name, client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    fetchAutomation: (token: string, client_id: number) => {
        // Perform a POST request to the login endpoint with the user data
        return ApiService.apiHit({
            url: 'get-automation',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    updateAutomation: (token: string, data: UpdateAutomationParams) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({
            url: 'update-automation',
            token,
            method: 'post',
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    updateBuildAutomation: (token: string, data: UpdateBuildAutomationParams) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({
            url: 'save-build-automation',
            token,
            method: 'post',
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    deleteAutomation: (token: string, automation_id: number) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({
            url: 'delete-automation',
            token,
            method: 'post',
            data: { automation_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    duplicateAutomation: (token: string, automation_id: number) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({
            url: 'duplicate-automation',
            token,
            method: 'post',
            data: { automation_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    checkPipeline: (token: string, pipeline_id: number, stage_id: number) => {
        // Perform a POST request to the logout endpoint with the authentication token
        return ApiService.apiHit({
            url: 'check-pipeline-stage-for-automation',
            token,
            method: 'post',
            data: { pipeline_id, stage_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default AutomationApi;
